import React, { useState, useEffect } from "react"
import { NumericFormat } from "react-number-format"
import { useSelector } from "react-redux"

import QuantitySelector from "@/components/cart/QuantitySelector"
import noImage from "@/public/images/image-missing.png"
import infoIcon from "@/public/icons/warning.svg"
import errorMsg from "@/public/images/error-qty.svg"
import {
  getPDPUrl,
  checkStock,
  getPresetUrl,
  isPriceZero,
  getProductDisplayName,
} from "@/utils/helper"

import _isEmpty from "lodash/isEmpty"
import useWindowResize from "@/hooks/useWindowResize"
import { getConfig } from "@/constants/config"
import {
  callOverlayEventInfoAnalytics,
  getProductInfoAnalytics,
  previewCartUpdateAnalytics,
} from "@/components/PreviewCart/v1/analytics"
import {
  removeCartItem,
  handleQuantityChange,
} from "@/components/cart/cartHelper"
import { selectCartState } from "@/store/features/cartSlice"

const PreviewCartTile = props => {
  const {
    i18n = {},
    data,
    checkoutMode,
    productEventInfo,
    presetConfigs = "",
  } = props || {}

  const {
    sku = "",
    isComboSku = false,
    isBundleParent = false,
    backOrder = false,
    category = "",
    id = "",
    bundleItemsCount = 0,
    availableQty = "",
    zipCode = "",
  } = data || {}

  const { outOfStock = "", availableToShipMsg = () => {} } = i18n

  const { qtyLoading = false } = useSelector(selectCartState)

  const [width] = useWindowResize()
  const [quantity, setQuantity] = useState(data.quantity)
  const [previewCartAlert, setPreviewCartAlert] = useState({
    show: false,
    mssg: "",
  })
  const [pdpUrl, setpdpUrl] = useState("")
  const [scene7AccountName, setScene7AccountName] = useState("")
  const [swatchUrl, setSwatchUrl] = useState("")
  const [siteName, setSiteName] = useState("")

  let totalArea = ""
  let productType = ""
  let totalLabel = ""

  useEffect(() => {
    setQuantity(data.quantity)
    if (data?.additionalData) setLabel()
  }, [data])

  useEffect(() => {
    getConfig().then(config => {
      constructPdpUrl()
      const {
        general: { scene7AccountName = "", swatchUrl = "", siteName = "" },
      } = config
      setScene7AccountName(scene7AccountName)
      setSwatchUrl(swatchUrl)
      setSiteName(siteName)
    })
  }, [])

  const handleCallBack = stock => {
    if (stock) {
      const mssg = availableToShipMsg(stock)
      setPreviewCartAlert({ show: true, mssg: `${mssg}` })
    } else {
      setPreviewCartAlert({ show: true, mssg: outOfStock })
    }
  }

  const handleChange = qty => {
    setQuantity(qty)
    setPreviewCartAlert({ show: false, mssg: "" })
    const payload = {
      id,
      sku,
      quantity: qty,
      isComboSku,
      isBundleParent,
      stockCheck: checkStock(backOrder, category),
      cb: handleCallBack,
      zipCode,
    }

    handleQuantityChange(payload)
  }

  const constructPdpUrl = async () => {
    let pdpUrl = await getPDPUrl(data.category, data.slug)
    if (
      data.category !== "Install Services" &&
      data.category !== "Design Services"
    ) {
      pdpUrl = `${pdpUrl}${pdpUrl?.includes("?type=part") ? "&" : "?"}skuId=${
        data.sku
      }`
    }
    setpdpUrl(pdpUrl)
  }

  const handleRemoveFromCart = () => {
    previewCartUpdateAnalytics(data)

    const payloadData = {
      id,
      quantity: quantity,
      isBundle: isComboSku,
      sku,
      isBundleParent,
      isComboSku,
    }

    removeCartItem(payloadData)
  }

  const handleProductClick = () => {
    const productInfo = getProductInfoAnalytics(data)
    callOverlayEventInfoAnalytics(pdpUrl, productEventInfo, productInfo, data)
  }

  const setLabel = () => {
    if (
      data.additionalData.roomInfo &&
      data.additionalData.roomInfo.length > 0
    ) {
      const roomInfoDetails = JSON.parse(data.additionalData.roomInfo[0])
      totalArea = roomInfoDetails.totalArea ? roomInfoDetails.totalArea : ""
      productType = roomInfoDetails.productType
        ? roomInfoDetails.productType
        : ""
      totalLabel = productType === "tile" ? i18n.squreFt : i18n.linearFt
    }
  }

  const presetConfigsData = presetConfigs ? JSON.parse(presetConfigs) : {}
  let imageUrl = data?.image

  if (
    !_isEmpty(presetConfigsData) &&
    siteName?.toLowerCase() === data?.brand?.toLowerCase()
  ) {
    const presetImageUrl = getPresetUrl(
      width,
      swatchUrl,
      data?.image,
      presetConfigsData,
      scene7AccountName
    )
    imageUrl = presetImageUrl
  }

  const discountVal = isPriceZero(data.discountedPricePerQuantity)
    ? data.discountedPriceProductLevel
    : data.discountedPricePerQuantity

  return (
    <>
      {props.comboChild && (
        <div className="preview-cart-item__item-of">
          {i18n.itemOf(props.comboChild.index, props.comboChild.count)}
        </div>
      )}
      <div className="preview-cart-item">
        <a href={pdpUrl} onClick={handleProductClick} data-testid="pdpClick">
          <img
            onError={e => (e.target.src = noImage?.src)}
            src={imageUrl || data.image}
            alt=""
            className="preview-cart-item__image"
          />
        </a>
        <div className="preview-cart-item__details">
          <div className="preview-cart-item__header">
            {data.brand && (
              <div className="preview-cart-item__brand">{data.brand}</div>
            )}
          </div>
          <a href={pdpUrl} onClick={handleProductClick}>
            <p className="preview-cart-item__name" data-testid="prodName">
              {getProductDisplayName(data)}
            </p>
          </a>
          {data.category !== "Install Services" &&
          data.category !== "Design Services" &&
          data.color !== "Not Applicable" &&
          !isBundleParent ? (
            <p className="preview-cart-item__finish" data-testid="prodColor">
              {i18n.colorFinish}: {data.color}
            </p>
          ) : null}
          {checkoutMode ? (
            <div className="preview-cart-item__skuId">
              {i18n.sku}:
              {data.customerFacingSKU ? data.customerFacingSKU : data.sku}
            </div>
          ) : null}

          {previewCartAlert.show && (
            <div className="preview-cart-item__alert">
              <img src={infoIcon?.src} alt={previewCartAlert.mssg} />
              <span>{previewCartAlert.mssg}</span>
            </div>
          )}
          {!previewCartAlert.show && data.outOfStock && availableQty < 1 ? (
            <div className="preview-cart-item__alert">
              <img src={infoIcon?.src} alt={i18n.outOfStock} />
              <span>{i18n.outOfStock}</span>
            </div>
          ) : null}
          {!previewCartAlert.show && data.outOfStock && availableQty > 0 ? (
            <div className="preview-cart-item__alert">
              <img src={errorMsg?.src} alt={availableToShipMsg(availableQty)} />
              <span>{availableToShipMsg(availableQty)}</span>
            </div>
          ) : null}
          {(productType === "tile" || productType === "trim") && (
            <div className="preview-cart-item__additional-Info">
              <span className="preview-cart-item__additional-Info-label">
                {i18n?.total?.toUpperCase()} {totalLabel?.toUpperCase()}
              </span>
              <span className="preview-cart-item__additional-Info-detail">
                : {totalArea}
              </span>
            </div>
          )}
          {isBundleParent ? (
            <p className="bundles-checkout-message">
              {i18n.includedItemsQty(bundleItemsCount)}
            </p>
          ) : null}

          <div className="preview-cart-item__footer">
            <div className="preview-cart-item__qty">
              {!checkoutMode ? (
                <QuantitySelector
                  source="cart-preview"
                  cartData={props.cartData}
                  productData={data}
                  size="small"
                  value={quantity}
                  onChange={qty => handleChange(qty)}
                  loading={qtyLoading || props.loading}
                  isBundleParent={isBundleParent}
                />
              ) : (
                <span
                  className="preview-cart-item__qty-label"
                  data-testid="prodQuantity"
                >{`${i18n.qtyLabel}: ${data.quantity}`}</span>
              )}
            </div>
            {!props.comboChild && (
              <div className="preview-cart-item__price-details">
                <p className="preview-cart-item__price">
                  <NumericFormat
                    value={
                      data.discountPrice > 0
                        ? data.discountPrice * Number(data.quantity ?? 1)
                        : data.totalPrice
                    }
                    displayType={"text"}
                    thousandSeparator={true}
                    aria-description={i18n.unitPrice}
                    prefix={props.currencySign}
                    decimalScale={2}
                    fixedDecimalScale={true}
                  />
                </p>
                {data.discountPrice > 0 && (
                  <div className="preview-cart-item__discount-container">
                    <p className="preview-cart-item__offPercent">
                      {data.discountPercent
                        ? `${data.discountPercent}${i18n.discountPercentage}`
                        : `${props.currencySign}${discountVal} ${i18n.off}`}
                    </p>
                    {data.discountPrice > 0 && (
                      <p className="preview-cart-item__discount">
                        <NumericFormat
                          value={data.unitPrice * data.quantity}
                          displayType={"text"}
                          aria-description={i18n.strikedPrice}
                          thousandSeparator={true}
                          prefix={props.currencySign}
                          decimalScale={2}
                          fixedDecimalScale={true}
                        />
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* {isBundleParent ? (
            <p className="preview-bundle-product-message text-right">
              {i18n.saveOnBundle("12")}
            </p>
          ) : null} */}

          {!checkoutMode && (
            <div
              className={`preview-cart-item__remove ${
                props.loading ? "--loading" : ""
              }`}
            >
              <span
                role="button"
                tabIndex="0"
                aria-label={`${data.name} ${i18n.remove}`}
                onClick={handleRemoveFromCart}
                data-testid={"removeFromCart"}
              >
                {i18n.remove}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default PreviewCartTile
